// RankBrief i18n — Dashboard, Settings, Modal, Auth
export const t = (lang, key, vars = {}) => {
  const str = (translations[lang] || translations.en)[key] || translations.en[key] || key;
  return str.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? '');
};

export const translations = {
  en: {
    // ── General ──────────────────────────────────────────────────────────────
    'save': 'Save',
    'saving': 'Saving...',
    'saved': '✓ Saved',
    'cancel': 'Cancel',
    'remove': 'Remove',
    'delete': 'Delete',
    'deleting': 'Deleting...',
    'loading': 'Loading...',
    'uploading': 'Uploading...',
    'error_generic': 'An error occurred. Please try again.',
    'error_network': 'Network error. Please try again.',
    'later': 'Set up later',

    // ── Auth ─────────────────────────────────────────────────────────────────
    'auth.welcome_back': 'Welcome back',
    'auth.sign_in_sub': 'Sign in to your RankBrief dashboard.',
    'auth.create_account': 'Create your account',
    'auth.register_sub': 'Get your first report in 60s. No credit card required.',
    'auth.sign_in': 'Sign in',
    'auth.register': 'Create account',
    'auth.forgot_password': 'Forgot password?',
    'auth.continue_google': 'Continue with Google',
    'auth.no_account': "Don't have an account?",
    'auth.sign_up_free': 'Sign up free',
    'auth.have_account': 'Already have an account?',
    'auth.waiting': 'Please wait…',

    // ── Dashboard ─────────────────────────────────────────────────────────────
    'dash.properties_title': 'Your Properties',
    'dash.first_website': 'Connect First Website',
    'dash.connect_google': 'Connect Google Account',
    'dash.add_property': '+ Add Property',
    'dash.more_account': '+ Another Account',
    'dash.upgrade_domains': 'Upgrade for more domains →',
    'dash.limit_reached': 'Limit reached ({{count}}/{{limit}})',
    'dash.connected': 'Connected {{date}}',
    'dash.settings_hint': 'Manage or remove properties → Settings',
    'dash.first_report': 'Your first report will be generated automatically on the 1st of next month.',
    'dash.no_summary': '🤖 No AI summary yet. Will be generated automatically with the next report.',
    'dash.seo_recs': 'SEO Recommendations:',
    'dash.seo_recs_sub': 'Concrete actions you can take this week',
    'dash.upgrade_loading': 'Loading...',
    'dash.choose_plan': 'Choose plan →',
    'dash.trial_title': 'Your free month is running',
    'dash.trial_sub': 'Choose a plan to continue receiving reports after your free month.',
    'dash.trial_choose': 'Choose a plan to keep receiving automatic monthly SEO reports.',
    'dash.ga4_hint': 'Google Analytics 4 shows you what visitors do on your website.',
    'dash.email_placeholder': 'client@example.com',
    'dash.email_hint': 'If set, the client receives the report in addition to you — with your White-Label branding.',
    'dash.recipient_email': 'Client Email (White-Label)',
    'dash.plan_monthly': 'monthly',
    'dash.plan_yearly': 'yearly',
    'dash.upgrade_title': 'Upgrade Plan',
    'dash.feature_1domain': '1 Domain',
    'dash.feature_3domains': '3 Domains',
    'dash.feature_10domains': '10 Domains',
    'dash.feature_monthly_report': 'Monthly PDF Report',
    'dash.feature_gsc_ga4': 'GSC + GA4 Data',
    'dash.feature_ai_summary': 'AI Summary',
    'dash.feature_email': 'Email Delivery',
    'dash.feature_all_basic': 'Everything in Basic',
    'dash.feature_all_pro': 'Everything in Pro',
    'dash.feature_white_label': 'White-Label Reports',
    'dash.feature_own_logo': 'Custom Logo',
    'dash.feature_priority': 'Priority Delivery',
    'dash.feature_client_mgmt': 'Client Management',
    'dash.feature_bulk': 'Bulk Reporting',
    'dash.feature_agency_brand': 'Agency Branding',
    'dash.billing_monthly': '/ month',
    'dash.billing_yearly_note': '(billed annually)',
    'dash.sign_out': 'Sign out',
    'dash.settings': '⚙ Settings',

    // ── Settings ──────────────────────────────────────────────────────────────
    'set.plan_billing': 'Plan & Billing',
    'set.plan_sub': 'Your current subscription and payment information',
    'set.current_plan': 'Current Plan',
    'set.status': 'Status',
    'set.status_active': '✅ Active',
    'set.status_inactive': 'inactive',
    'set.status_frozen': '🔒 Frozen',
    'set.billing_portal': '↗ Billing Portal',
    'set.choose_plan': 'Looking for a different plan?',
    'set.properties': 'Properties',
    'set.domains_connected': '{{count}} / {{limit}} Domains connected',
    'set.add_property': '+ Add Property',
    'set.connect_google': '+ Connect Google Account',
    'set.remove_property': 'Remove Property',
    'set.confirm_delete_property': 'Delete this property and all associated report data permanently. This cannot be undone.',
    'set.ga4_connected': '✅ GA4 connected: {{id}}',
    'set.ga4_hint': 'Numbers only — e.g. 123456789. Find it in Google Analytics → Admin → Property Settings.',
    'set.branding': 'Branding & White-Label',
    'set.branding_sub': 'Logo, colors, font and sender name in the monthly PDF report',
    'set.branding_locked': '🔒 White-Label is available from the Pro plan.',
    'set.branding_upgrade': 'Upgrade to Pro',
    'set.company_name': 'Company Name',
    'set.logo': 'Company Logo',
    'set.logo_hint': 'PNG, JPG or SVG · max. 500 KB · Recommended: 400×120px, transparent background',
    'set.logo_upload': '↑ Upload Logo',
    'set.logo_uploading': 'Uploading...',
    'set.primary_color': 'Primary Color (Headlines, KPI accents)',
    'set.accent_color': 'Accent Color (Borders, backgrounds)',
    'set.font': 'Report Font',
    'set.font_hint': 'Font is loaded in the PDF via Google Fonts',
    'set.reply_to': 'Reply-To Email',
    'set.reply_to_hint': 'Your clients can reply to this address',
    'set.report_language': 'Report Language',
    'set.preview': 'Preview',
    'set.preview_ai': '🤖 AI Summary: Last month your website showed strong growth. The click-through rate improved significantly...',
    'set.save_branding': 'Save Branding',
    'set.branding_saved': 'Branding saved — takes effect on the next report. ✓',
    'set.google_accounts': 'Connected Google Accounts',
    'set.google_accounts_sub': 'These Google accounts are linked to your RankBrief account.',
    'set.no_google': 'No Google account connected yet. Connect one via the dashboard.',
    'set.promo_title': '🎟️ Redeem Promo Code',
    'set.promo_sub': 'Have a code? Upgrade here without a credit card.',
    'set.promo_placeholder': 'e.g. 2026RANKBRIEFPROMO',
    'set.promo_redeem': 'Redeem →',
    'set.promo_checking': 'Checking…',
    'set.account': 'Account',
    'set.account_sub': 'Manage your account settings',
    'set.email': 'Email address',
    'set.change_password': 'Change Password',
    'set.current_password': 'Current Password',
    'set.new_password': 'New Password',
    'set.save_password': 'Save Password',
    'set.password_saved': 'Password changed successfully.',
    'set.delete_account': 'Delete Account',
    'set.delete_account_sub': 'Permanently deletes your account, all properties and all report data.',
    'set.delete_cancel_first': 'Please cancel your subscription via the Billing Portal first.',
    'set.delete_confirm_title': 'Really delete account?',
    'set.delete_confirm_active': 'You still have an active subscription ({{plan}}). Please cancel it in the Billing Portal first — then you can delete your account.',
    'set.delete_confirm_text': 'Your account, all properties and all report data will be permanently deleted. This cannot be undone.',
    'set.connected_date': 'Connected {{date}}',
    'set.portal_error': 'Error opening portal. Please try again.',
    'set.property_deleted': 'Property deleted.',
    'set.error_delete': 'Error deleting.',
    'set.logo_too_large': 'Logo too large — max. 500 KB (currently {{size}} KB).',
    'set.downgrade_select': 'Your new plan supports {{max}} domain(s). Please select which ones to keep:',

    // ── PropertySelectModal ───────────────────────────────────────────────────
    'modal.step1_title': 'Which websites to track?',
    'modal.step1_sub_slots': 'You can connect {{remaining}} more of {{limit}} {{prop}} ({{plan}} plan).',
    'modal.step1_sub_full': 'All {{limit}} {{prop}} of your plan are in use. Deselect one to activate another.',
    'modal.property': 'property',
    'modal.properties': 'properties',
    'modal.sc_domain_hint': '💡 sc-domain: properties cover all subdomains & protocols (recommended). URL properties only cover the exact path.',
    'modal.active_badge': 'active',
    'modal.token_error': '⚠️ Token expired or error loading. Please reconnect this Google account.',
    'modal.no_sites': 'No GSC properties found in this account.',
    'modal.connect_btn': '{{count}} {{prop}} connect →',
    'modal.min_one': 'Select at least one property',
    'modal.add_google': 'Connect another Google Account',
    'modal.later': 'Set up later',
    'modal.step2_title': 'Connect Google Analytics',
    'modal.step2_sub': 'GA4 adds Sessions, Users and Engagement Rate to your report — what visitors actually do on your site, not just how they arrive via Google.',
    'modal.ga4_placeholder': 'GA4 Property ID (optional)',
    'modal.ga4_hint': 'Find it in Google Analytics → Admin → Property Settings → Property ID. Numbers only — not G-XXXXXXXX. Make sure the GA4 property matches the domain.',
    'modal.ga4_already': '✅ GA4 already connected: {{id}}',
    'modal.finish': 'Complete Setup →',
    'modal.checking': '⏳ Checking GA4…',
    'modal.skip_ga4': 'Continue without GA4',
    'modal.skip_ga4_invalid': 'Continue without GA4 (invalid ID)',
  },

  de: {
    // ── General ──────────────────────────────────────────────────────────────
    'save': 'Speichern',
    'saving': 'Speichert...',
    'saved': '✓ Gespeichert',
    'cancel': 'Abbrechen',
    'remove': 'Entfernen',
    'delete': 'Löschen',
    'deleting': 'Wird gelöscht...',
    'loading': 'Lädt...',
    'uploading': 'Lädt hoch...',
    'error_generic': 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
    'error_network': 'Netzwerkfehler. Bitte versuche es erneut.',
    'later': 'Später einrichten',

    // ── Auth ─────────────────────────────────────────────────────────────────
    'auth.welcome_back': 'Willkommen zurück',
    'auth.sign_in_sub': 'Melde dich in deinem RankBrief Dashboard an.',
    'auth.create_account': 'Konto erstellen',
    'auth.register_sub': 'Ersten Report in 60s erhalten. Keine Kreditkarte erforderlich.',
    'auth.sign_in': 'Anmelden',
    'auth.register': 'Konto erstellen',
    'auth.forgot_password': 'Passwort vergessen?',
    'auth.continue_google': 'Mit Google fortfahren',
    'auth.no_account': 'Noch kein Konto?',
    'auth.sign_up_free': 'Kostenlos registrieren',
    'auth.have_account': 'Bereits ein Konto?',
    'auth.waiting': 'Bitte warten…',

    // ── Dashboard ─────────────────────────────────────────────────────────────
    'dash.properties_title': 'Deine Properties',
    'dash.first_website': 'Erste Website verbinden',
    'dash.connect_google': 'Google-Konto verbinden',
    'dash.add_property': '+ Property hinzufügen',
    'dash.more_account': '+ Weiteres Konto',
    'dash.upgrade_domains': 'Upgrade für mehr Domains →',
    'dash.limit_reached': 'Limit erreicht ({{count}}/{{limit}})',
    'dash.connected': 'Verbunden {{date}}',
    'dash.settings_hint': 'Properties verwalten oder entfernen → Einstellungen',
    'dash.first_report': 'Der erste Report wird automatisch am 1. des nächsten Monats generiert.',
    'dash.no_summary': '🤖 Noch keine KI-Zusammenfassung vorhanden. Wird automatisch beim nächsten Report generiert.',
    'dash.seo_recs': 'SEO-Empfehlungen:',
    'dash.seo_recs_sub': 'Konkrete Maßnahmen was du diese Woche tun kannst',
    'dash.upgrade_loading': 'Lädt...',
    'dash.choose_plan': 'Plan wählen →',
    'dash.trial_title': 'Dein kostenloser Monat läuft',
    'dash.trial_sub': 'Wähle einen Plan um nach dem Freimonat weiterhin Reports zu erhalten.',
    'dash.trial_choose': 'Wähle einen Plan um weiterhin automatische monatliche SEO-Reports zu erhalten.',
    'dash.ga4_hint': 'Google Analytics 4 zeigt dir was Besucher auf deiner Website tun.',
    'dash.email_placeholder': 'kunde@beispiel.de',
    'dash.email_hint': 'Wenn gesetzt, bekommt der Endkunde den Report zusätzlich zu dir – mit deinem White-Label Branding als Absender.',
    'dash.recipient_email': 'Kunden-Email (White-Label)',
    'dash.plan_monthly': 'monatlich',
    'dash.plan_yearly': 'jährlich',
    'dash.upgrade_title': 'Plan upgraden',
    'dash.feature_1domain': '1 Domain',
    'dash.feature_3domains': '3 Domains',
    'dash.feature_10domains': '10 Domains',
    'dash.feature_monthly_report': 'Monatlicher PDF-Report',
    'dash.feature_gsc_ga4': 'GSC + GA4 Daten',
    'dash.feature_ai_summary': 'KI-Zusammenfassung',
    'dash.feature_email': 'Email-Versand',
    'dash.feature_all_basic': 'Alles in Basic',
    'dash.feature_all_pro': 'Alles in Pro',
    'dash.feature_white_label': 'White-Label Reports',
    'dash.feature_own_logo': 'Eigenes Logo',
    'dash.feature_priority': 'Priority Delivery',
    'dash.feature_client_mgmt': 'Client Management',
    'dash.feature_bulk': 'Bulk Reporting',
    'dash.feature_agency_brand': 'Agency Branding',
    'dash.billing_monthly': '/ Monat',
    'dash.billing_yearly_note': '(jährlich abgerechnet)',
    'dash.sign_out': 'Abmelden',
    'dash.settings': '⚙ Einstellungen',

    // ── Settings ──────────────────────────────────────────────────────────────
    'set.plan_billing': 'Plan & Billing',
    'set.plan_sub': 'Dein aktuelles Abonnement und Zahlungsinformationen',
    'set.current_plan': 'Aktueller Plan',
    'set.status': 'Status',
    'set.status_active': '✅ Aktiv',
    'set.status_inactive': 'inactive',
    'set.status_frozen': '🔒 Eingefroren',
    'set.billing_portal': '↗ Billing Portal',
    'set.choose_plan': 'Anderen Plan wählen?',
    'set.properties': 'Properties',
    'set.domains_connected': '{{count}} / {{limit}} Domains verbunden',
    'set.add_property': '+ Property hinzufügen',
    'set.connect_google': '+ Google-Konto verbinden',
    'set.remove_property': 'Property entfernen',
    'set.confirm_delete_property': 'Diese Property und alle zugehörigen Report-Daten werden unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.',
    'set.ga4_connected': '✅ GA4 verbunden: {{id}}',
    'set.ga4_hint': 'Nur Ziffern – z.B. 123456789. Zu finden in Google Analytics → Admin → Property-Einstellungen.',
    'set.branding': 'Branding & White-Label',
    'set.branding_sub': 'Logo, Farben, Schriftart und Absendername im monatlichen PDF-Report',
    'set.branding_locked': '🔒 White-Label ist ab dem Pro-Plan verfügbar.',
    'set.branding_upgrade': 'Auf Pro upgraden',
    'set.company_name': 'Firmenname',
    'set.logo': 'Firmenlogo',
    'set.logo_hint': 'PNG, JPG oder SVG · max. 500 KB · Empfohlen: 400×120px, transparenter Hintergrund',
    'set.logo_upload': '↑ Logo hochladen',
    'set.logo_uploading': 'Lädt hoch...',
    'set.primary_color': 'Primärfarbe (Headlines, KPI-Akzente)',
    'set.accent_color': 'Akzentfarbe (Rahmen, Hintergründe)',
    'set.font': 'Report-Schriftart',
    'set.font_hint': 'Schriftart wird im PDF via Google Fonts geladen',
    'set.reply_to': 'Antwort-Email',
    'set.reply_to_hint': 'Deine Kunden können auf diese Adresse antworten',
    'set.report_language': 'Report-Sprache',
    'set.preview': 'Vorschau',
    'set.preview_ai': '🤖 KI-Zusammenfassung: Im vergangenen Monat verzeichnete deine Website starkes Wachstum. Die Klickrate verbesserte sich deutlich...',
    'set.save_branding': 'Branding speichern',
    'set.branding_saved': 'Branding gespeichert – wirkt beim nächsten Report. ✓',
    'set.google_accounts': 'Verbundene Google-Konten',
    'set.google_accounts_sub': 'Diese Google-Accounts sind mit deinem RankBrief-Account verknüpft.',
    'set.no_google': 'Noch kein Google-Konto verbunden. Verbinde ein Konto über das Dashboard.',
    'set.promo_title': '🎟️ Promo-Code einlösen',
    'set.promo_sub': 'Hast du einen Code? Hier upgraden ohne Kreditkarte.',
    'set.promo_placeholder': 'z.B. 2026RANKBRIEFPROMO',
    'set.promo_redeem': 'Einlösen →',
    'set.promo_checking': 'Prüfe…',
    'set.account': 'Account',
    'set.account_sub': 'Verwalte deine Account-Einstellungen',
    'set.email': 'E-Mail-Adresse',
    'set.change_password': 'Passwort ändern',
    'set.current_password': 'Aktuelles Passwort',
    'set.new_password': 'Neues Passwort',
    'set.save_password': 'Passwort speichern',
    'set.password_saved': 'Passwort erfolgreich geändert.',
    'set.delete_account': 'Account löschen',
    'set.delete_account_sub': 'Löscht deinen Account, alle Properties und alle Report-Daten dauerhaft.',
    'set.delete_cancel_first': 'Bitte kündige zuerst dein Abo über das Billing Portal, bevor du den Account löschst.',
    'set.delete_confirm_title': 'Account wirklich löschen?',
    'set.delete_confirm_active': 'Du hast noch ein aktives Abo ({{plan}}). Bitte kündige es im Billing Portal – danach kannst du deinen Account löschen.',
    'set.delete_confirm_text': 'Dein Account, alle Properties und alle Report-Daten werden dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.',
    'set.connected_date': 'Verbunden {{date}}',
    'set.portal_error': 'Fehler beim Öffnen des Portals. Bitte erneut versuchen.',
    'set.property_deleted': 'Property gelöscht.',
    'set.error_delete': 'Fehler beim Löschen.',
    'set.logo_too_large': 'Logo zu groß – max. 500 KB (aktuell {{size}} KB).',
    'set.downgrade_select': 'Dein neuer Plan unterstützt {{max}} Domain(s). Bitte wähle aus, welche Domains du behalten möchtest:',

    // ── PropertySelectModal ───────────────────────────────────────────────────
    'modal.step1_title': 'Welche Properties tracken?',
    'modal.step1_sub_slots': 'Du kannst noch {{remaining}} von {{limit}} {{prop}} verbinden ({{plan}}-Plan).',
    'modal.step1_sub_full': 'Du hast alle {{limit}} {{prop}} deines Plans belegt. Wähle ab um eine andere zu aktivieren.',
    'modal.property': 'Property',
    'modal.properties': 'Properties',
    'modal.sc_domain_hint': '💡 sc-domain: Properties decken alle Subdomains & Protokolle ab (empfohlen). URL-Properties nur den exakten Pfad.',
    'modal.active_badge': 'aktiv',
    'modal.token_error': '⚠️ Token abgelaufen oder Fehler beim Laden. Bitte Google-Konto neu verbinden.',
    'modal.no_sites': 'Keine GSC Properties in diesem Konto gefunden.',
    'modal.connect_btn': '{{count}} {{prop}} verbinden →',
    'modal.min_one': 'Mindestens eine Property wählen',
    'modal.add_google': 'Weiteres Google-Konto verbinden',
    'modal.later': 'Später einrichten',
    'modal.step2_title': 'Google Analytics verbinden',
    'modal.step2_sub': 'GA4 ergänzt deinen Report um Sessions, Nutzer und Engagement Rate — also was Besucher auf deiner Website wirklich tun, nicht nur wie sie über Google kommen.',
    'modal.ga4_placeholder': 'GA4 Property ID (optional)',
    'modal.ga4_hint': 'Zu finden in Google Analytics → Admin → Property Settings → Property ID. Nur Zahlen — nicht G-XXXXXXXX. Achte darauf dass die GA4 Property zur jeweiligen Domain passt.',
    'modal.ga4_already': '✅ GA4 bereits verbunden: {{id}}',
    'modal.finish': 'Einrichtung abschließen →',
    'modal.checking': '⏳ GA4 wird geprüft…',
    'modal.skip_ga4': 'Ohne GA4 fortfahren',
    'modal.skip_ga4_invalid': 'Ohne GA4 fortfahren (ID ungültig)',
  },
};

export default t;
